import { useMemo, useState } from 'react';

import CharacterGrid from '../components/CharacterGrid';
import CharacterCard from '../components/CharacterCard';
import CreateCharacterCard from '../components/CreateCharacterCard';
import { getMediaUrl, isCustomCharacter } from '../lib/utils';
import { useTracking, UI_EVENTS } from '../hooks/useTracking';
import { useUserInfo } from '../hooks/useUserInfo';
import type { Database } from '../types/database';
import { SEO } from '../components/SEO';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { BookHeart, Sparkles } from 'lucide-react';

type Character = Database['eh']['Tables']['characters']['Row'];

function ContactsPage() {
  const { trackEvent } = useTracking();
  const { user, characters: allCharacters, isLoading, error } = useUserInfo();

  const [activeCardId, setActiveCardId] = useState<string | null>(null);

  // Filter characters to only show user-created ones (excluding parent_char_id)
  const userCharacters = useMemo(() => {
    if (!allCharacters) return [];
    
    return allCharacters
      .filter(isCustomCharacter)
      .map((c) => ({
        ...c,
        profile_media_fpaths: (c.profile_media_fpaths || []).map(getMediaUrl),
      }));
  }, [allCharacters]);

  const handleCharacterClick = (character: Character) => {
    const { id, age, gender, style, url_slug } = character;
    try {
      trackEvent(UI_EVENTS.CHARACTER_CLICKED, { 
        id, 
        age, 
        gender, 
        style, 
        url_slug,
        source: 'ContactsPage' 
      });
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error tracking character click:', error);
    }
  };

  // Show loading state while checking user authentication
  if (isLoading) {
    return (
      <>
        <SEO
          title="My AI Contacts - EveningHoney.AI"
          description="View and chat with your created AI characters"
          ogUrl="/contacts"
        />
        <div className="max-w-full 2xl:container mx-auto flex-grow">
          <div className="py-8 px-4">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2 flex items-center justify-center gap-3">
                <BookHeart className="size-8" />
                <span>My AI Contacts</span>
              </h1>
              <p className="text-zinc-400 text-lg">
                <span>Your created AI companions</span>
              </p>
            </div>
            <CharacterGrid loading characters={[]} />
          </div>
        </div>
      </>
    );
  }

  // Show auth required message if not logged in
  if (!user) {
    return (
      <>
        <SEO
          title="My AI Contacts - EveningHoney.AI"
          description="View and chat with your created AI characters"
          ogUrl="/contacts"
        />
        <div className="max-w-full 2xl:container mx-auto flex-grow">
          <div className="py-8 px-4">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2 flex items-center justify-center gap-3">
                <BookHeart className="size-8" />
                <span>My Contacts</span>
              </h1>
              <p className="text-zinc-400 text-lg">
                <span>Created companions</span>
              </p>
            </div>
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-6">
              <div className="text-center">
                <div className="text-xl text-zinc-300 mb-2">Please log in to view your AI contacts</div>
                <div className="text-zinc-400">You need to be logged in to access your created characters</div>
              </div>
              <Button asChild size="lg" className="bg-amber-600 hover:bg-amber-500">
                <Link to="/">
                  <span>Explore Characters</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Show error state
  if (error) {
    return (
      <>
        <SEO
          title="My AI Contacts - EveningHoney.AI"
          description="View and chat with your created AI characters"
          ogUrl="/contacts"
        />
        <div className="max-w-full 2xl:container mx-auto flex-grow">
          <div className="py-8 px-4">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2 flex items-center justify-center gap-3">
                <BookHeart className="size-8" />
                <span>My Contacts</span>
              </h1>
              <p className="text-zinc-400 text-lg">
                <span>Created companions</span>
              </p>
            </div>
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-6">
              <div className="text-center">
                <div className="text-xl text-red-400 mb-2">{error}</div>
                <div className="text-zinc-400">Please try again later</div>
              </div>
              <Button 
                onClick={() => window.location.reload()} 
                size="lg" 
                variant="outline"
              >
                <span>Retry</span>
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="My AI Contacts - EveningHoney.AI"
        description="View and chat with your created AI characters"
        ogUrl="/contacts"
      />
      <div className="max-w-full 2xl:container mx-auto flex-grow">
        <div className="py-8 px-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center justify-center gap-3">
              <BookHeart className="size-8" />
              <span>My Contacts</span>
              {userCharacters.length > 0 && (
                 <span>{`(${userCharacters.length})`}</span>
               )}
            </h1>
          </div>

          {userCharacters.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-6">
              <div className="text-center">
                <div className="text-xl text-zinc-300 mb-2">No contacts yet</div>
                <div className="text-zinc-400 mb-6">Create your first AI companion to get started</div>
                <CreateCharacterCard />
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-pink-600 hover:bg-pink-500">
                  <Link to="/create-character">
                    <Sparkles className="size-5" />
                    <span>Create Your AI</span>
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/">
                    <span>Explore Characters</span>
                  </Link>
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-4 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
              <CreateCharacterCard />
              {userCharacters.map((char) => {
                const route = `/c/${char.url_slug}`;
                return (
                  <Link
                    key={char.id}
                    to={route}
                    style={{ textDecoration: 'none' }}
                    onClick={() => handleCharacterClick(char as Character)}
                  >
                    <CharacterCard
                      character={char as Character}
                      activeCardId={activeCardId}
                      setActiveCardId={setActiveCardId}
                    />
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ContactsPage; 